import React, { useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import './ConfirmDialog.css';

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  hideCancel?: boolean;
  confirmVariant?: 'primary' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

const buttonsIn = (dialog: globalThis.HTMLDialogElement) => Array.from(
  dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'),
);
const isTopDialog = (dialog: globalThis.HTMLDialogElement) => (
  Array.from(document.querySelectorAll('dialog.dialog-overlay[open]')).at(-1) === dialog
);
const inertElements = new Map<globalThis.Element, { count: number; original: string | null }>();
let openDialogs = 0;
let originalOverflow = '';

function lockBodyScroll() {
  if (!openDialogs) originalOverflow = document.body.style.overflow;
  openDialogs += 1;
  document.body.style.overflow = 'hidden';

  return () => { openDialogs -= 1; if (!openDialogs) document.body.style.overflow = originalOverflow; };
}

function lockBackground(dialog: globalThis.HTMLDialogElement) {
  const background = Array.from(document.body.children).filter((element) => element !== dialog);

  background.forEach((element) => {
    const lock = inertElements.get(element) ?? { count: 0, original: element.getAttribute('inert') };

    inertElements.set(element, { ...lock, count: lock.count + 1 });
    element.setAttribute('inert', '');
  });

  return () => background.forEach((element) => {
    const lock = inertElements.get(element);

    if (!lock) return;
    if (lock.count > 1) {
      lock.count -= 1;

      return;
    }
    if (lock.original === null) element.removeAttribute('inert');
    else element.setAttribute('inert', lock.original);
    inertElements.delete(element);
  });
}

// Native modal dialogs make the rest of the page inert; the fallback supports older embedded browsers.
function openDialog(dialog: globalThis.HTMLDialogElement) {
  if (typeof dialog.showModal === 'function') {
    dialog.showModal();

    return () => { if (dialog.open) dialog.close(); };
  }
  const unlock = lockBackground(dialog);

  dialog.setAttribute('open', '');

  return () => {
    dialog.removeAttribute('open');
    unlock();
  };
}

function useDialogInteraction(ref: React.RefObject<globalThis.HTMLDialogElement | null>, onCancel: () => void) {
  const cancel = useRef(onCancel);

  useLayoutEffect(() => { cancel.current = onCancel; }, [onCancel]);
  // eslint-disable-next-line max-lines-per-function, max-statements
  useLayoutEffect(() => {
    const dialog = ref.current;

    if (!dialog) return undefined;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const close = openDialog(dialog);
    const unlockScroll = lockBodyScroll();
    const focusFirst = () => buttonsIn(dialog)[0]?.focus();
    const keepFocus = (event: globalThis.FocusEvent) => {
      if (isTopDialog(dialog) && !dialog.contains(event.target as Node)) focusFirst();
    };
    // eslint-disable-next-line max-statements
    const onKey = (event: KeyboardEvent) => {
      if (!isTopDialog(dialog) || !['Escape', 'Tab'].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.key === 'Escape') {
        cancel.current();

        return;
      }
      const buttons = buttonsIn(dialog);
      const index = buttons.findIndex((button) => button === document.activeElement);
      const next = (index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length;

      buttons[next]?.focus();
    };

    focusFirst();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('focusin', keepFocus, true);

    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('focusin', keepFocus, true);
      close();
      unlockScroll();
      if (previous?.isConnected && !previous.closest('[inert]')) previous.focus();
    };
  }, [ref]);
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', hideCancel = false,
  confirmVariant = 'primary', onConfirm, onCancel,
}) => {
  const ref = useRef<globalThis.HTMLDialogElement>(null);
  const id = useId();

  useDialogInteraction(ref, onCancel);

  return createPortal(
    <dialog ref={ref} className="dialog-overlay" aria-modal="true" aria-labelledby={`${id}-title`}
      aria-describedby={`${id}-message`} onCancel={(event) => { event.preventDefault(); onCancel(); }}
      onClick={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) onCancel(); }}>
      <div className="dialog">
        <div className={`dialog-icon dialog-icon--${confirmVariant}`} aria-hidden="true">
          <i className={`fa-solid ${confirmVariant === 'danger' ? 'fa-triangle-exclamation' : 'fa-circle-info'}`} />
        </div>
        <h3 className="dialog-title" id={`${id}-title`}>{title}</h3>
        <p className="dialog-message" id={`${id}-message`}>{message}</p>
        <div className="dialog-actions">
          {!hideCancel && <button type="button" className="dialog-btn dialog-btn--secondary" onClick={onCancel}>
            {cancelLabel}</button>}
          <button type="button" className={`dialog-btn dialog-btn--${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}</button>
        </div>
      </div>
    </dialog>, document.body,
  );
};
